import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { ApiHookProps } from '@/@types/api.types';
import EToast from '@/components/everridge/EToast';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { useTranslation } from 'react-i18next';
import {
    AppReleaseDetailsResponseDto,
    CreateAppReleaseRequestDto,
    CreateResponseDto,
    UpdateAppReleaseRequestDto,
} from '../types/releases-dto';
import { DateTimeHelper } from '@/utils/dateTimeHelper';

type Props = {} & ApiHookProps;

export const useReleaseManageApi = ({ id }: Props) => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const {
        data: releaseDetailsData,
        isLoading: isLoadingReleaseDetailsData,
        isError: isErrorReleaseDetailsData,
    } = useQuery({
        queryKey: [ApiConfig.queries.features.releases.queryKeys.details, id],
        queryFn: async (): Promise<AppReleaseDetailsResponseDto> => {
            const url = ApiConfig.queries.features.releases.endpoints.get.replace('{id}', id || '');
            const response = await axiosInstance.get<AppReleaseDetailsResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
        enabled: !!id,
    });

    const {
        mutateAsync: createRelease,
        isPending: isCreatingRelease,
        isError: isErrorCreatingRelease,
    } = useMutation({
        mutationFn: async (dto: CreateAppReleaseRequestDto) => {
            const url = ApiConfig.queries.features.releases.endpoints.create;
            const response = await axiosInstance.post<CreateResponseDto>(url, {
                ...dto,
                releasedAt: DateTimeHelper.getDateOnly(dto.releasedAt),
            });
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.releases.queryKeys.list],
            });
            EToast({
                variant: 'success',
                message: t('main.releases.toasts.create.success.message'),
                description: t('main.releases.toasts.create.success.description'),
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.releases.toasts.create.error.message'),
                description: t('main.releases.toasts.create.error.description'),
            });
        },
    });

    const {
        mutateAsync: updateRelease,
        isPending: isUpdatingRelease,
        isError: isErrorUpdatingRelease,
    } = useMutation({
        mutationFn: async (dto: UpdateAppReleaseRequestDto) => {
            const url = ApiConfig.queries.features.releases.endpoints.update.replace(
                '{id}',
                id || '',
            );
            const response = await axiosInstance.put<UpdateAppReleaseRequestDto>(url, {
                ...dto,
                releasedAt: DateTimeHelper.getDateOnly(dto.releasedAt),
            });
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.releases.queryKeys.list],
            });
            EToast({
                variant: 'success',
                message: t('main.releases.toasts.update.success.message'),
                description: t('main.releases.toasts.update.success.description'),
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.releases.toasts.update.error.message'),
                description: t('main.releases.toasts.update.error.description'),
            });
        },
    });

    const {
        mutateAsync: deleteRelease,
        isPending: isDeletingRelease,
        isError: isErrorDeletingRelease,
    } = useMutation({
        mutationFn: async () => {
            const url = ApiConfig.queries.features.releases.endpoints.update.replace(
                '{id}',
                id || '',
            );
            const response = await axiosInstance.delete(url);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.releases.queryKeys.list],
            });
            EToast({
                variant: 'success',
                message: t('main.releases.toasts.delete.success.message'),
                description: t('main.releases.toasts.delete.success.description'),
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.releases.toasts.delete.error.message'),
                description: t('main.releases.toasts.delete.error.description'),
            });
        },
    });

    const {
        mutateAsync: sendRelease,
        isPending: isSendingRelease,
        isError: isErrorSendingRelease,
    } = useMutation({
        mutationFn: async (id: string) => {
            const url = ApiConfig.queries.features.releases.endpoints.send.replace(
                '{id}',
                id || '',
            );
            const response = await axiosInstance.post<UpdateAppReleaseRequestDto>(url);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.releases.queryKeys.list],
            });
            EToast({
                variant: 'success',
                message: t('main.releases.toasts.send.success.message'),
                description: t('main.releases.toasts.send.success.description'),
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.releases.toasts.send.error.message'),
                description: t('main.releases.toasts.send.error.description'),
            });
        },
    });

    return {
        releaseDetailsData,
        isLoadingReleaseDetailsData,
        isErrorReleaseDetailsData,
        createRelease,
        isCreatingRelease,
        isErrorCreatingRelease,
        updateRelease,
        isUpdatingRelease,
        isErrorUpdatingRelease,
        deleteRelease,
        isDeletingRelease,
        isErrorDeletingRelease,
        sendRelease,
        isSendingRelease,
        isErrorSendingRelease,
    };
};
